import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, getApiClient } from '@signalvote/api-client';
import type { FulfilVoterDataOrderBody, WorkOrderListParams } from '@signalvote/api-client';
import type {
  CreateVoterDataOrderRequest,
  CreateWorkOrderRequest,
  RequoteVoterDataOrderRequest,
  UpdateWorkOrderRequest,
  WorkOrderCheckoutRequest,
  WorkOrderCheckoutResponse,
  WorkOrderDto,
  WorkOrderTransitionRequest,
} from '@signalvote/shared';
import { ErrorCode, WorkOrderStatus, isVoterDataImporting } from '@signalvote/shared';
import { queryKeys } from '@/lib/queryKeys';

const api = getApiClient();

/** How often an order whose list is importing into voters is read again, until the import ends. */
const IMPORT_POLL_MS = 5_000;

/**
 * Fetch a single Work Order with its status, details, and the live recipient count
 * (contactable voters). Campaign-scoped. The recipient count is display-only and
 * resolved fresh server-side on every read, so this is not aggressively cached. A Voter data
 * order whose list is importing is read again until the import ends, unless the caller sets its
 * own interval.
 */
export function useWorkOrder(id: string | undefined, options: { refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: queryKeys.workOrders.detail(id ?? ''),
    queryFn: () => api.workOrders.get(id!),
    enabled: !!id,
    refetchInterval:
      options.refetchInterval ?? ((query) => (isVoterDataImporting(query.state.data?.voterData?.importStatus) ? IMPORT_POLL_MS : false)),
  });
}

/** The checkout this page already raised for a Work Order, if any: its invoice, lines and client secret. */
export function useWorkOrderCheckoutResult(id: string) {
  return useQuery<WorkOrderCheckoutResponse>({
    queryKey: queryKeys.workOrders.checkout(id),
    queryFn: () => Promise.reject(new Error('A checkout is only ever raised by useWorkOrderCheckout.')),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/**
 * Raises and finalizes a Work Order's invoice for the card form. The result is kept per order, so
 * the page pays that same invoice however many times the person presses Pay.
 */
export function useWorkOrderCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkOrderCheckoutRequest }) => api.workOrders.checkout(id, body),
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(queryKeys.workOrders.checkout(id), result);
      queryClient.setQueryData(queryKeys.workOrders.detail(id), result.workOrder);
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
    onError: async (error, { id }) => {
      // A changed list price was stored when it refused, and a paid send has moved on: read either again.
      if (!(error instanceof ApiError)) return;
      if (error.code === ErrorCode.CheckoutAlreadyPaid) {
        // The paid order lands in the cache first, so its checkout preview is not asked for again.
        await queryClient.refetchQueries({ queryKey: queryKeys.workOrders.detail(id), exact: true });
      }
      if (error.code === ErrorCode.VoterDataPriceChanged || error.code === ErrorCode.CheckoutAlreadyPaid) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
      }
    },
  });
}

/**
 * Fetch the campaign's Work Orders jobs list — newest-first, paginated, optionally
 * filtered by raw status. The jobs-list grouping (by what each is awaiting) is derived
 * client-side off the returned status.
 */
export function useWorkOrders(params?: WorkOrderListParams) {
  return useQuery({
    queryKey: queryKeys.workOrders.list(params),
    queryFn: () => api.workOrders.list(params),
  });
}

/**
 * Create a text-send Work Order in Draft (the self-serve "New text send"). On success,
 * invalidates every Work Orders query so the fresh Draft appears in the jobs list, and
 * returns the created Work Order so the caller can navigate to its build screen. The body
 * never carries `amount` — the price is server-owned.
 */
export function useCreateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateWorkOrderRequest) => api.workOrders.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/**
 * Persist a partial edit of a Draft Work Order's message / send date / send time (the
 * self-serve build screen's on-blur save). On success, invalidates every Work Orders query
 * so the detail re-reads the persisted state (what "Submit & Pay" will invoice) and the jobs
 * list reflects the updated schedule. The backend rejects a too-long message or a
 * campaign-local-past send date/time with 400; callers surface that to the admin.
 */
export function useUpdateWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateWorkOrderRequest }) =>
      api.workOrders.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/**
 * Apply one hand-driven operator transition (mark vendor-paid, complete, cancel, fail)
 * to a Work Order. On success, invalidates every Work Orders query so the jobs list and
 * the detail view reflect the new status. The backend rejects a stale
 * `expectedConcurrencyVersion` with 409; callers surface that to the operator.
 */
export function useWorkOrderTransition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkOrderTransitionRequest }) =>
      api.workOrders.transition(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/**
 * What a Draft's checkout would bill now, line by line at Stripe's prices. The web never prices a
 * send itself; it shows these. The server prices only a Draft, so this asks only while the cached
 * order reads Draft. The check runs when a refetch is due, not at render, so a checkout that lands
 * Invoiced in the cache and then invalidates every Work Orders query never asks again.
 */
export function useWorkOrderCheckoutPreview(id: string | undefined, enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.workOrders.checkoutPreview(id ?? ''),
    queryFn: () => api.workOrders.checkoutPreview(id!),
    enabled: () =>
      !!id &&
      enabled &&
      queryClient.getQueryData<WorkOrderDto>(queryKeys.workOrders.detail(id))?.status === WorkOrderStatus.Draft,
  });
}

/**
 * Attach (or replace) the single image on a draft Work Order at the token the caller holds. The
 * returned token is written into the cached order so the next write sends it; the detail query is
 * then re-read for the fresh preview URL, also after a refusal so a stale token can be retried.
 */
export function useAttachWorkOrderImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file, expectedConcurrencyVersion }: { id: string; file: File; expectedConcurrencyVersion: number }) =>
      api.workOrders.attachImage(id, file, expectedConcurrencyVersion),
    onSuccess: ({ concurrencyVersion }, { id }) => {
      queryClient.setQueryData<WorkOrderDto>(queryKeys.workOrders.detail(id), (order) =>
        order ? { ...order, concurrencyVersion } : order,
      );
    },
    onSettled: (_data, _error, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
    },
  });
}

/**
 * Uploads a CSV of phone numbers as a Draft Text send's Recipient list. The server cleans it and
 * answers with the order, carrying the ready and removed counts, which lands in the detail cache.
 */
export function useUploadWorkOrderRecipients() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file, expectedConcurrencyVersion }: { id: string; file: File; expectedConcurrencyVersion: number }) =>
      api.workOrders.uploadRecipients(id, file, expectedConcurrencyVersion),
    onSuccess: (workOrder) => {
      queryClient.setQueryData(queryKeys.workOrders.detail(workOrder.id), workOrder);
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/**
 * Places a Voter data order as a Draft, quoted afresh by the server; the body carries no count or
 * price. A retry with the same idempotency key resolves to the Draft that key already made.
 * Invoicing is a separate step, once the customer confirms the quote.
 */
export function useCreateVoterDataOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateVoterDataOrderRequest) => api.workOrders.createVoterData(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/**
 * Re-quotes the Draft a builder placed with the list it now shows, at the token the builder holds;
 * the result carries the new one. 409 once the Draft is checked out or its token is stale.
 */
export function useRequoteVoterDataOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RequoteVoterDataOrderRequest }) => api.workOrders.requoteVoterData(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/** Reads a Work Order afresh from the server, refreshing its cached detail. */
export function useRefetchWorkOrder() {
  const queryClient = useQueryClient();

  return (id: string) =>
    queryClient.fetchQuery({ queryKey: queryKeys.workOrders.detail(id), queryFn: () => api.workOrders.get(id), staleTime: 0 });
}

/**
 * Paid Voter data orders awaiting fulfilment, across every campaign in the subsidiary, oldest
 * first. Platform admins only.
 */
export function useVoterDataQueue() {
  return useQuery({
    queryKey: queryKeys.workOrders.voterDataQueue(),
    queryFn: () => api.workOrders.voterDataQueue(),
  });
}

/**
 * Fulfils a Paid Voter data order: uploads the CSV exported from L2 with its delivered count,
 * moving it Paid → Complete. Platform admins only. On success, invalidates every Work Orders query
 * (including the queue) so the order drops out of the queue and its detail shows Complete.
 */
export function useFulfilVoterDataOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: FulfilVoterDataOrderBody }) =>
      api.workOrders.fulfilVoterData(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/**
 * Fetches a fresh short-lived link to a Complete Voter data order's file; the caller sends the
 * browser to it. A 410 means the file expired since the order was read, so the order is refetched
 * and its detail flips to "file expired".
 */
export function useDownloadVoterDataFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.workOrders.voterDataDownload(id),
    onError: (error, id) => {
      if (error instanceof ApiError && error.status === 410) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
      }
    },
  });
}

/**
 * Imports a Complete Voter data order's file into the campaign's voters. The caller follows the
 * import's progress on the voter import progress stream; the order is refetched so its detail shows
 * the import. A 410 means the file expired since the order was read, and a 409
 * `VoterDataAlreadyImported` that an import already runs or finished (delivery starts one): either
 * way the refetched order shows where it stands. A 402 (lapsed subscription) is handled by the API
 * client, which marks the campaign lapsed.
 */
export function useImportVoterDataFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.voterDataImport.import(id),
    onSuccess: (_started, id) => queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) }),
    onError: (error, id) => {
      if (
        error instanceof ApiError
        && (error.status === 410 || error.code === ErrorCode.VoterDataAlreadyImported)
      ) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
      }
    },
  });
}

/**
 * Signs a fresh download of a delivered voter list's zip through its emailed link, with no session. Each
 * press signs a new one (they last minutes), so the emailed link itself never goes stale. A wrong token is a
 * 404 `VoterListLinkNotFound`; an expired file a 410.
 */
export function useVoterListDownload(token: string) {
  return useMutation({
    mutationFn: () => api.voterListLink.get(token),
  });
}

/** Signs a fresh download of a Text send's results report through its status link, with no session. */
export function useTextSendResultsDownload(token: string) {
  return useMutation({
    mutationFn: () => api.textSendStatusLink.getResultsReport(token),
  });
}

/** Signs a fresh download of a Text send's results report for the signed-in campaign; the caller sends the browser to it. */
export function useDownloadResultsReport() {
  return useMutation({
    mutationFn: (id: string) => api.workOrders.resultsReport(id),
  });
}

/**
 * Attaches or replaces a Complete Text send's results report PDF (platform admins). The returned order,
 * carrying its new token, lands in the detail cache.
 */
export function useAttachResultsReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, file, expectedConcurrencyVersion }: { id: string; file: File; expectedConcurrencyVersion: number }) =>
      api.workOrders.attachResultsReport(id, file, expectedConcurrencyVersion),
    onSuccess: (workOrder) => {
      queryClient.setQueryData(queryKeys.workOrders.detail(workOrder.id), workOrder);
      queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.all });
    },
  });
}

/**
 * A Text send's status, read through its status link with no session. A wrong token is a 404 with
 * `StatusLinkNotFound`, which is not retried. Refreshed every minute while open.
 */
export function useTextSendStatus(token: string) {
  return useQuery({
    queryKey: queryKeys.textSendStatus.byToken(token),
    queryFn: () => api.textSendStatusLink.get(token),
    retry: (failures, error) => !(error instanceof ApiError && error.status === 404) && failures < 3,
    refetchInterval: 60_000,
  });
}
